import React from "react";
import {
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import {
  formatFacilityDropdownLabel,
  getFacilityHierarchyId,
} from "@utils/facilityUtils";
import { FacilityPickerOptionContent } from "../FacilityPickerOptionContent";
import { FacilityTypeValueStack } from "../FacilityTypeValueStack";
import { FN } from "./facilityNetworkTheme";

type GroupedOption = {
  facility: FacilityHierarchyInput;
  depth: number;
  isParent: boolean;
  key: string;
};

type Props = {
  selectedFacilityId: number | "";
  selectedFacility?: FacilityHierarchyInput;
  groupedFacilityOptions: GroupedOption[];
  onFacilityChange: (event: SelectChangeEvent<number | "">) => void;
};

const fieldSx = {
  fontSize: 13,
  bgcolor: "#fdffff",
  border: `1px solid #99d4e1`,
  borderRadius: "8px",
  height: FN.headerFieldHeight,
  minHeight: FN.headerFieldHeight,
  maxHeight: FN.headerFieldHeight,
  boxSizing: "border-box",
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "100%",
    minHeight: "unset !important",
    py: 0,
    px: FN.headerFieldPx,
    boxSizing: "border-box",
  },
};

const labelSx = {
  color: FN.pressed,
  fontSize: 14,
  fontWeight: 500,
  whiteSpace: "nowrap",
  flexShrink: 0,
};

function compactFacilityLabel(facility: FacilityHierarchyInput): string {
  return formatFacilityDropdownLabel(facility) || facility.facility_name || "Facility";
}

export default function FacilityNetworkFacilityHeader({
  selectedFacilityId,
  selectedFacility,
  groupedFacilityOptions,
  onFacilityChange,
}: Props) {
  return (
    <Box
      sx={{
        bgcolor: FN.topBarBg,
        border: `1px solid ${FN.topBarBorder}`,
        borderRadius: "12px",
        px: { xs: 1.5, md: 2 },
        py: 0.5,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        gap: { xs: 1, md: 2 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography sx={labelSx}>Selected Facility</Typography>
        <Select<number | "">
          value={selectedFacilityId}
          onChange={onFacilityChange}
          displayEmpty
          renderValue={(selected) => {
            if (selected === "" || selected == null) {
              return (
                <Typography sx={{ color: FN.muted, fontSize: 13 }}>
                  Select a facility
                </Typography>
              );
            }
            const id = selected as number;
            const opt = groupedFacilityOptions.find(
              (o) => getFacilityHierarchyId(o.facility) === id,
            );
            const facility = opt?.facility ?? selectedFacility;
            return (
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: FN.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
              >
                {facility ? compactFacilityLabel(facility) : ""}
              </Typography>
            );
          }}
          sx={{
            ...fieldSx,
            flex: 1,
            maxWidth: { md: 420 },
          }}
        >
          {groupedFacilityOptions.map(({ facility, depth, isParent, key }) => {
            const optionId = getFacilityHierarchyId(facility);
            if (optionId == null) return null;
            return (
              <MenuItem key={key} value={optionId}>
                <Box sx={{ pl: depth === 0 ? 0 : 3, width: "100%" }}>
                  <FacilityPickerOptionContent facility={facility} isParent={isParent} />
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </Box>

      {selectedFacility && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography sx={labelSx}>Facility Type</Typography>
          <Box
            sx={{
              flex: 1,
              height: FN.headerFieldHeight,
              minHeight: FN.headerFieldHeight,
              maxHeight: FN.headerFieldHeight,
              border: `1px solid #99d4e1`,
              borderRadius: "8px",
              bgcolor: "#f8fbfb",
              px: FN.headerFieldPx,
              py: 0,
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <FacilityTypeValueStack facility={selectedFacility} compact />
          </Box>
        </Box>
      )}
    </Box>
  );
}
